import { CustomizeButton as CustomizeButtonView } from "@/design-system/CustomizeButton/CustomizeButton";
import { solveSettingsActiveSelector } from "@/redux/sbcs/sbcFiltersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

import { SolveSettingsPanel } from "./SolveSettingsPanel";

/* Wide enough for the two-column layout without the lanes cramping. */
const PANEL_WIDTH = 720;

/**
 * The solve settings behind the Customize button. On touch devices the sheet
 * has its own header and a pinned Apply; the dot says a setting is away from
 * its default.
 *
 * With `onApply` (the solution page) the panel runs live on both layouts and
 * its primary button re-solves; clicking outside or minimizing keeps the
 * changes without solving.
 */
export const CustomizeButton = ({
  compact = false,
  stretch = false,
  placement = "bottom",
  onApply,
  applyLabel,
  disabled = false,
}: {
  /** Icon only. */
  compact?: boolean;
  stretch?: boolean;
  /** Desktop: below the button (a toolbar) or beside it (a rail). */
  placement?: "bottom" | "right";
  /** Runs after the primary button closes the surface. */
  onApply?: () => void;
  /** The primary button's label; Apply when absent. */
  applyLabel?: string;
  /** Greyed and inert, e.g. while a solve runs: its result would ignore a change made now. */
  disabled?: boolean;
}) => {
  const { t } = useTranslation("sbcs");
  const active = useSelector(solveSettingsActiveSelector);
  return (
    <CustomizeButtonView
      label={t("customize.title")}
      active={active}
      compact={compact}
      stretch={stretch}
      placement={placement}
      width={PANEL_WIDTH}
      disabled={disabled}
      panel={({ compact: sheet, close }) =>
        sheet ? (
          <SolveSettingsPanel
            compact
            mode={onApply ? "live" : "draft"}
            onApply={() => {
              close();
              onApply?.();
            }}
            onClose={close}
            onDismiss={close}
            applyLabel={applyLabel}
          />
        ) : (
          // Live: changes land as made, so closing by any route keeps them.
          // Apply is still there as the obvious way out.
          <SolveSettingsPanel
            mode="live"
            onApply={() => {
              close();
              onApply?.();
            }}
            onDismiss={close}
            applyLabel={applyLabel}
          />
        )
      }
    />
  );
};
